import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import {
  X,
  ArrowsOut,
  ArrowsIn,
  PaperPlaneRight,
  ChatCircleDots,
  Plus,
  Trash,
  CaretLeft,
  SpinnerGap,
} from '@phosphor-icons/react';
import {
  MilesChatMessage,
  MilesChatConversation,
  MilesChatPreset,
} from './milesTypes';
import MilesMessageList from './MilesMessageList';
import MilesAvatar from './MilesAvatar';
import MilesThinkingIndicator from './MilesThinkingIndicator';
import MilesPresetPrompts from './MilesPresetPrompts';

/**
 * Formats a conversation title from the created_at timestamp
 * e.g., "Started at 2:44PM on Dec 3rd"
 */
function formatConversationTitle(createdAt: string | undefined): string {
  if (!createdAt) return 'New conversation';

  try {
    const date = new Date(createdAt);

    // Format time as "2:44PM"
    const time = date
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(' ', '');

    // Format date as "Dec 3rd"
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const suffix = getDaySuffix(day);

    return `Started at ${time} on ${month} ${day}${suffix}`;
  } catch {
    return 'New conversation';
  }
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

interface MilesChatWindowProps {
  isExpanded: boolean;
  onClose: () => void;
  onToggleExpand: () => void;
  // From useMilesChat hook
  messages: MilesChatMessage[];
  conversations: MilesChatConversation[];
  presetPrompts: MilesChatPreset[];
  currentConversationId: string | null;
  isLoading: boolean;
  isSending: boolean;
  isPolling: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  selectConversation: (conversationId: string) => void;
  startNewConversation: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Chat window component for MILES AI assistant.
 * Supports both collapsed (mini) and expanded (fullscreen) modes.
 * On mobile screens (< 600px), always renders fullscreen - no collapsed view.
 */
function MilesChatWindow({
  isExpanded,
  onClose,
  onToggleExpand,
  messages,
  conversations,
  presetPrompts,
  currentConversationId,
  isLoading,
  isSending,
  isPolling,
  error,
  sendMessage,
  selectConversation,
  startNewConversation,
  deleteConversation,
  clearError,
}: MilesChatWindowProps) {
  const [inputValue, setInputValue] = useState('');
  const [showConversations, setShowConversations] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Detect mobile screen size (< 600px / md breakpoint)
  // On mobile, always use fullscreen - no collapsed view
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 599px)');

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // On mobile, force fullscreen mode
  const effectiveIsExpanded = isMobile || isExpanded;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending || isPolling) return;
    const message = inputValue.trim();
    setInputValue('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePresetClick = (promptText: string) => {
    setInputValue(promptText);
    inputRef.current?.focus();
  };

  const handleDeleteConversation = async (
    e: React.MouseEvent,
    conversationId: string
  ) => {
    e.stopPropagation();
    await deleteConversation(conversationId);
  };

  // Determine window classes based on expanded state
  // On mobile, always fullscreen (effectiveIsExpanded is true)
  const windowClasses = effectiveIsExpanded
    ? 'fixed inset-0 z-[100] flex flex-col bg-white'
    : 'fixed bottom-6 right-6 z-[100] flex h-[600px] w-[400px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl';

  return (
    <div className={windowClasses}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-primary to-primary/90 px-4 py-3 text-white">
        <div className="flex items-center gap-3">
          {showConversations && (
            <button
              onClick={() => setShowConversations(false)}
              className="rounded-lg p-1 hover:bg-white/10"
              aria-label="Back to chat"
            >
              <CaretLeft size={20} weight="bold" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <MilesAvatar
              size="sm"
              loop={true}
              className="rounded-lg bg-white/10 p-0.5"
            />
            <div>
              <h2 className="text-[18px] font-semibold">MILES</h2>
              <p className="text-[13px] text-white/80">
                Your AI Business Assistant
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Conversations button */}
          <button
            onClick={() => setShowConversations(!showConversations)}
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label="View conversations"
            title="Conversations"
          >
            <ChatCircleDots size={20} />
          </button>

          {/* New conversation button */}
          <button
            onClick={startNewConversation}
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label="New conversation"
            title="New conversation"
          >
            <Plus size={20} />
          </button>

          {/* Expand/Collapse button - hidden on mobile (always fullscreen) */}
          {!isMobile && (
            <button
              onClick={onToggleExpand}
              className="rounded-lg p-2 hover:bg-white/10"
              aria-label={
                isExpanded ? 'Collapse window' : 'Expand to fullscreen'
              }
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ArrowsIn size={20} /> : <ArrowsOut size={20} />}
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label="Close chat"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {showConversations ? (
          /* Conversations List */
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="mb-3 text-[16px] font-semibold text-gray-900">
              Conversations
            </h3>
            {conversations.length === 0 ? (
              <p className="text-center text-[14px] text-gray-500">
                No conversations yet. Start a new chat!
              </p>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => {
                      selectConversation(conv.conversation_id);
                      setShowConversations(false);
                    }}
                    className={`group flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                      currentConversationId === conv.conversation_id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-[15px] font-medium text-gray-900">
                        {conv.title || formatConversationTitle(conv.created_at)}
                      </p>
                      {conv.last_message_preview && (
                        <p className="truncate text-[14px] text-gray-500">
                          {conv.last_message_preview}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) =>
                        handleDeleteConversation(e, conv.conversation_id)
                      }
                      className="ml-2 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                      aria-label="Delete conversation"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading && messages.length === 0 ? (
                <MilesThinkingIndicator
                  variant="centered"
                  message="Loading your conversation"
                />
              ) : messages.length === 0 ? (
                /* Welcome Screen with Hero Animation */
                <div className="flex min-h-full flex-col items-center text-center">
                  {/* Hero Animation - The MILES mascot */}
                  <div className="mb-4">
                    <MilesAvatar size="hero" loop={true} speed={1} />
                  </div>

                  {/* Welcome Text */}
                  <h3 className="mb-2 text-[22px] font-semibold text-gray-900">
                    Hey there! I'm MILES
                  </h3>
                  <p className="mb-6 max-w-[280px] text-[15px] text-gray-600">
                    Your AI-powered business assistant. Ask me about your
                    finances, fleet, drivers, or anything else!
                  </p>

                  {/* Preset Prompts with Category Filter */}
                  {presetPrompts.length > 0 && (
                    <div className="w-full max-w-full pb-8">
                      <MilesPresetPrompts
                        prompts={presetPrompts}
                        onSelectPrompt={handlePresetClick}
                      />
                    </div>
                  )}
                </div>
              ) : (
                /* Message List */
                <>
                  <MilesMessageList messages={messages} />
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mx-4 mb-2 flex items-center justify-between rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
                <span>{error}</span>
                <button
                  onClick={clearError}
                  className="ml-2 hover:text-red-800"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Input Area */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask MILES anything..."
                  className="max-h-32 min-h-[72px] flex-1 resize-none rounded-xl border border-gray-300 px-4 py-3 text-[15px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  rows={3}
                  disabled={isSending || isPolling}
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isSending || isPolling}
                  className={`relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 ${
                    inputValue.trim() && !isSending && !isPolling
                      ? 'overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/25 before:to-transparent'
                      : ''
                  }`}
                  aria-label="Send message"
                >
                  {isSending || isPolling ? (
                    <SpinnerGap size={22} className="animate-spin" />
                  ) : (
                    <PaperPlaneRight size={22} weight="fill" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-center text-[13px] text-gray-400">
                MILES can make mistakes. Verify important information.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MilesChatWindow;
